import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { GoogleButton } from '@/Domain/components/Auth/SocialButton/GoogleButton';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { EnvelopIcon } from '@/Domain/components/icons/Envelop.icon';
import LockLineIcon from '@/Domain/components/icons/LockLineIcon';
import { SocialPlatformEnum } from '@/Domain/enum/SocialPlatform.enum';
import { useRegister } from '@/Domain/hooks/Auth/useRegister.hook';
import { useSocialRegister } from '@/Domain/hooks/Auth/useSocialRegister.hook';

export const RegisterPage = () => {
  const { t } = useTranslation('register');
  const { track } = useAnalytics();
  const { search } = useLocation();

  const { control, useV2, register, handleSubmit, onSubmit, errors, onVerify, isPending } =
    useRegister();
  const { handleSocialRegister, isNavigating } = useSocialRegister();

  const handleTrack = () =>
    track('register_back_click', {
      event_category: 'User Registration',
      event_label: 'register_form',
    });

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      <div className="grid gap-5">
        {/* Social Login */}
        <div className="grid gap-4">
          <GoogleButton
            onClick={(event) => handleSocialRegister(event, SocialPlatformEnum.Google)}
            disabled={isPending || isNavigating}
            isLoading={isNavigating}
          />
        </div>

        {/* Divider */}
        <div className="relative flex pt-1 items-center">
          <div className="flex-grow border-t border-border"></div>
          <span className="mx-4 flex-shrink text-xs font-medium uppercase tracking-wide text-foreground-subtle">
            {t('or')}
          </span>
          <div className="flex-grow border-t border-border"></div>
        </div>

        {/* Register Form */}
        <form
          id="register-email-form"
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <GoogleReCaptcha onVerify={onVerify} />

          {/* Email Field */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium text-foreground">
              {t('form.email')}
            </label>
            <Input
              id="email"
              size="lg"
              {...register('username')}
              placeholder={t('form.email_placeholder')}
              error={!!errors.username}
              autoFocus
              autoComplete="email"
              startIcon={<EnvelopIcon className="h-4 w-4 text-foreground-subtle" />}
            />
            {errors.username && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.username.message}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div className="space-y-1.5">
            <label htmlFor="password1" className="text-sm font-medium text-foreground">
              {t('form.password')}
            </label>
            <PasswordInput
              id="password1"
              size="lg"
              placeholder={t('form.password_placeholder')}
              {...register('password1')}
              error={!!errors.password1}
              autoComplete="new-password"
              startIcon={<LockLineIcon className="h-4 w-4 text-foreground-subtle" />}
            />
            {errors.password1 && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.password1.message}
              </p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-1.5">
            <label htmlFor="password2" className="text-sm font-medium text-foreground">
              {t('form.confirm_password')}
            </label>
            <PasswordInput
              id="password2"
              size="lg"
              placeholder={t('form.password_placeholder')}
              {...register('password2')}
              error={!!errors.password2}
              autoComplete="new-password"
              startIcon={<LockLineIcon className="h-4 w-4 text-foreground-subtle" />}
            />
            {errors.password2 && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.password2.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            variant="dark"
            size="lg"
            type="submit"
            disabled={isPending}
            className="w-full mt-1"
          >
            {isPending ? <Spinner /> : t('button.submit')}
          </Button>

          {/* reCAPTCHA v2 Fallback */}
          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox onChange={onChange} isInvalid={invalid} />
              )}
            />
          )}
        </form>

        {/* Terms of Service */}
        <p className="text-center text-sm text-muted-foreground">
          <Trans
            i18nKey="register:terms_of_use"
            components={{
              terms_link: (
                <Link
                  className="font-medium text-foreground transition-colors hover-underline"
                  to="https://www.quickchat.ai/terms"
                  target="_blank"
                />
              ),
              privacy_link: (
                <Link
                  className="font-medium text-foreground transition-colors hover-underline"
                  to="https://www.quickchat.ai/privacy"
                  target="_blank"
                />
              ),
            }}
          />
        </p>

        {/* Login Link */}
        <div className="border-t border-border pt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('have_account')}{' '}
            <Link
              className="font-medium text-foreground transition-colors hover-underline"
              to={`/${search}`}
              onClick={handleTrack}
            >
              {t('link.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
